import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { BellIcon, DoubleCheckmarkIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Popover from '@repo/ui/popover'
import LineTab from '@repo/ui/tabs'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { getNotifications } from '../../api/notifications/get-notifications'
import { markNotificationAsRead } from '../../api/notifications/mark-notification-as-read'
import { markNotificationsAsRead } from '../../api/notifications/mark-notifications-as-read'
import { Notification as NotificationType } from '../../lib/types/models/notification/notification'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import Notification from './notification'

const NotificationsToggler: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0)

  const { data, isPending, refetch } = useQuery({
    queryKey: ['notifications', activeTab],
    queryFn: () =>
      getNotifications({
        filter: { read: activeTab === 0 ? undefined : false }
      }),
    refetchInterval: 20000
  })

  const { mutate: markNotificationAsReadMutation } = useMutation({
    mutationFn: markNotificationAsRead,
    onSuccess: () => {
      refetch()
    },
    onError: showAxiosError
  })
  const { mutate: markNotificationsAsReadMutation } = useMutation({
    mutationFn: markNotificationsAsRead,
    onSuccess: () => {
      refetch()
    },
    onError: showAxiosError
  })

  const hasUnreadNotifications = useMemo(() => {
    const unreadNotifications = data?.data.filter((notification) => !notification.read)
    return unreadNotifications && unreadNotifications.length > 0
  }, [data])

  const formattedNotifications = useMemo(() => {
    return data?.data.reduce(
      (acc, notification) => {
        const date = new Date(notification.createdAt).toDateString()
        if (!acc[date]) acc[date] = []
        acc[date].push(notification)
        return acc
      },
      {} as Record<string, NotificationType[]>
    )
  }, [data])

  const handleNotificationClick = (notification: NotificationType) => {
    markNotificationAsReadMutation(notification.id)
  }

  return (
    <Popover
      triggerComponent={(open) => (
        <button
          className={cn(
            'w-8 h-8 relative rounded-full flex items-center justify-center transition-colors',
            {
              'bg-grey-light-active/70': open,
              'hover:bg-grey-light-hover': !open
            }
          )}
        >
          {hasUnreadNotifications && (
            <div className="w-1.5 h-1.5 rounded-full bg-red-secondary absolute top-1 right-1" />
          )}
          <BellIcon color={tailwindColors.grey.DEFAULT} />
        </button>
      )}
      title="Notifications"
      position={'end'}
      className="min-w-[360px] max-w-[360px] max-h-[550px] overflow-y-auto mobile:min-w-[33px] mobile:max-w-[300px]"
      alignOffset={window.innerWidth < 768 ? -Math.floor(window.innerWidth / 5.4) : 0}
    >
      <LineTab
        activeTab={activeTab}
        onTabChange={(index) => setActiveTab(index)}
        tabs={[{ label: 'Toutes' }, { label: 'Non lues' }]}
        customTabComponent={
          <button
            className="py-2.5 ml-1 cursor-pointer flex flex-1 items-center gap-1"
            onClick={() => {
              markNotificationsAsReadMutation()
            }}
          >
            <DoubleCheckmarkIcon />
            <Typography
              className="whitespace-nowrap mobile:whitespace-normal"
              variant="label-medium"
              color={tailwindColors.primary.DEFAULT}
            >
              Marquer toutes comme lues
            </Typography>
          </button>
        }
      />
      <div className="flex flex-col gap-2 mt-3">
        {isPending ? (
          <Loader center />
        ) : data?.data.length === 0 ? (
          <div className="flex justify-center items-center flex-col gap-6 pb-9 mt-5">
            <img src="/notification.png" width={160} />

            <div className="flex flex-col gap-2">
              <Typography variant="label-semibold" className="text-center">
                Pas de notification
              </Typography>
              <Typography variant="small-medium" className="text-center text-grey-lighter">
                Vous verrez les notifications lorsqu'elles seront disponibles.
              </Typography>
            </div>
          </div>
        ) : (
          Object.entries(formattedNotifications ?? {}).map(
            ([date, grouppedNotifications], index) => (
              <div className="flex flex-col p-2" key={index}>
                <Typography variant="label-medium" className="pl-2 pb-1" title={date}>
                  {DateFormatter.dateToText(new Date(date))}
                </Typography>
                {grouppedNotifications.map((notification) => (
                  <Notification
                    key={notification.id}
                    notification={notification}
                    onClick={() => {
                      handleNotificationClick(notification)
                    }}
                    className={cn({
                      'border-b border-grey-light':
                        grouppedNotifications.indexOf(notification) !==
                        grouppedNotifications.length - 1
                    })}
                  />
                ))}
              </div>
            )
          )
        )}
      </div>
    </Popover>
  )
}

export default NotificationsToggler
